import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ToastNotificationsService } from '@reconcept-platform/shared/ui/core/toast-notifications';
import { BugsnagClient } from '@reconcept-platform/shared/util';

@Injectable()
export class ErrorSinkService extends ErrorHandler {
  private injector = inject(Injector);

  private bugsnagClient = inject(BugsnagClient);

  constructor() {
    super();
  }

  override handleError(error: any): void {
    if (error && typeof error === 'object' && 'code' in error && error.name === 'AbortError') {
      // ignore abort errors
      return;
    }

    // pass error thhrough to the console
    ErrorHandler.prototype.handleError.call(this, error);

    if (this.errorIsNoSession(error?.message)) {
      this.notifyAndRouteToLogin();
      return;
    }

    const errorId = (Math.random() + 1).toString(36).slice(2);
    // bugsnag is not started in dev, so we can call it here safely
    this.reportToBugsnag(error, { code: { error: errorId } });
    this.notifyUser(errorId);
  }

  private reportToBugsnag(error: any, metaData: { code: { error: string } }) {
    // Bugnsag calls the initial implementation of ErrorHandler, that will result in the error showing in the console.
    this.bugsnagClient?.notify(error, metaData, 'Error');
  }

  private notifyUser(errorId: string) {
    const notificationsService = this.injector.get(ToastNotificationsService, null);
    // only show extra notification if error was unexpected
    if (notificationsService?.notifications.length === 0) {
      notificationsService?.errorTranslated({
        titleKey: 'qz-something-went-wrong-in-application-zm',
        messageKey: 'qz-error-has-occurred-in-application-please-notify-zm',
        errorCode: errorId,
      });
    }
  }

  private errorIsNoSession(message?: string) {
    return message?.includes('no_session');
  }

  private notifyAndRouteToLogin() {
    const router = this.injector.get(Router),
      notificationsService = this.injector.get(ToastNotificationsService, null);

    notificationsService?.errorTranslated({
      titleKey: 'qz-no-session-found-zm',
      messageKey: 'qz-you-may-have-logged-out-used-expired-link-zm',
    });

    router?.navigateByUrl('/login', { replaceUrl: true });
  }
}
